import { useDispatch, useSelector } from "react-redux";
import { BudgetModal } from "../../components/quickbooks/BudgetModal";
import { BudgetLoadingKeys, createBudgetRequest, deleteBudgetRequest, duplicateBudgetRequest, getBudgetCards, getBudgetSettings, getConnectedForecasts, updateConnectedForecasts } from "../../slices/quickbooks/budgetSlice";
import { ForecastEditModal } from "../../components/quickbooks/ForecastEditModal";
import { createForecastRequest, deleteForecastRequest, ForecastLoadingKeys, getDemandPlanningOptions, getForecastOptions, getForecasts } from "../../slices/quickbooks/forecastSlice";
import { LoaderSpinner } from "../../components/LoaderSpinner";
import { TanStackTable } from "../../components/TanStackTable";
import { ArrowDownTrayIcon, ArrowPathIcon, ArrowTrendingUpIcon, ChartBarSquareIcon, Cog8ToothIcon, CurrencyDollarIcon, DocumentDuplicateIcon, EyeIcon, PencilIcon, PlusIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ConfrimModal } from "../../components/ConfrimModal";
import { useNavigate } from "react-router-dom";
import { OverlayGrayBackground } from "../../components/OverlayGrayBackground";
import { useEffect, useState } from "react";
import { ROUTES } from "../../consts/general";
import { ProfitLossTable } from "../../components/quickbooks/ProfitLossTable";
import { CardTotal } from "../../components/quickbooks/CardTotal";
import { getTableData } from "../../consts/forecastPlanning";
import { SelectDropdown } from "../../components/SelectDropdown";
import { ForecastPlanningLoadingKeys, getPlannedForecastsOptions } from "../../slices/quickbooks/forecastPlanningSlice";
import { arrayHasItems } from "../../helpers/common";
import { DuplicateBudgetModal } from "../../components/DuplicateBudgetModal";
import { getBudgetForecastsData, getBudgetForecastsOptions, ProfitAndLossNewLoadingKeys } from "../../slices/quickbooks/profitAndLossNewSlice";
import { getReportCategories } from "../../slices/quickbooks/reportCategories";
import { BudgetSettingsSidebar } from "../../components/budget/BudgetSettingsSidebar";
import { LOWER_IS_BETTER } from "../../helpers/profitAndLoss/varianceDirection";

const BudgetForecast = ({
    access
}) => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [budget, setBudget] = useState(false);
    const [forecast, setForecast] = useState(false);
    const [budgetIdToDelete, setBudgetIdToDelete] = useState("");
    const [isOpenViewBudgetAndForecast, setIsOpenViewBudgetAndForecast] = useState(false);
    const [deleteBudgetModalIsOpen, setDeleteBudgetModalIsOpen] = useState(false);
    const [deleteForecastModalIsOpen, setDeleteForecastModalIsOpen] = useState(false);
    const [forecastIdToDelete, setForecastIdToDelete] = useState("");
    const [filteredForecasts, setFilteredForecasts] = useState([]);
    const [exportingTable, setExportingTable] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [currentBudget, setCurrentBudget] = useState(null);
    const [duplicateBudget, setDuplicateBudget] = useState(null);
    const [budgetSettingsSidebarOpen, setBudgetSettingsSidebarOpen] = useState(false);

    const {
        budgetOptions,
        budgetAndForecastData: profitAndLossReportByForecast,
        loading: budgetForecastLoading,
        loadingKeys: budgetForecastLoadingKeys,
      } = useSelector((state) => state.profitAndLossNew);
    const {
        loading: forecastPlanningLoading,
        loadingKeys: forecastPlanningLoadingKeys,
        plannedForecastsOptions,
    } = useSelector((state) => state.forecastPlanning);
    const {
        loading: forecastLoading,
        loadingKeys: forecastLoadingKeys,
        firstLoadComplete: forecastFirstLoadComplete,
        forecastOptions,
        demandPlanningOptions,
        forecasts
    } = useSelector((state) => state.forecast);
    const {
        loading: budgetLoading,
        loadingKeys: budgetLoadingKeys,
        firstLoadComplete: budgetFirstLoadComplete,
        budgetCards,
        budgetSettings
    } = useSelector((state) => state.budget);
    const {
        categories: reportCategories
    } = useSelector((state) => state.reportCategories);
    useEffect(() => {
        dispatch(getForecasts({}));
        dispatch(getBudgetCards({}));
        dispatch(getForecastOptions({}));
        dispatch(getReportCategories({}));
        dispatch(getBudgetForecastsOptions({}));
        dispatch(getPlannedForecastsOptions({}));
        dispatch(getDemandPlanningOptions({}));
        dispatch(getBudgetSettings({}));
    }, []);
    useEffect(() => {
        const currentForecastOptions = arrayHasItems(forecastOptions)
            ? forecastOptions.map(opt => ({
                label: !opt.value ? opt.label : `${opt.label} (F)`,
                value: `${opt.value}-defaultforecast`,
                forecastYear: opt.period,
            }))
            : [];
        const currentPlannedForecastsOptions = arrayHasItems(plannedForecastsOptions)
            ? plannedForecastsOptions.map(opt => ({
                label: `${opt.label} (M)`,
                value: `${opt.value}-plannedforecast`,
                forecastYear: opt.forecast_year,
            }))
            : [];
        const currentDemandPlanningOptions = arrayHasItems(demandPlanningOptions)
            ? demandPlanningOptions.map(opt => ({
                label: opt.publish_status === 'PUBLISHED'
                    ? `${opt.label} (D)`
                    : `${opt.label} (D – Draft)`,
                value: `${opt.value}-demandplan`,
                forecastYear: opt.forecast_year,
                publishStatus: opt.publish_status,
            }))
            : [];
        const selectedValue = budget?.forecast_id;
        const budgetYear = budget?.period;
        const options = [
            ...currentForecastOptions,
            ...currentPlannedForecastsOptions,
            ...currentDemandPlanningOptions,
        ].filter((option) =>
            (option.publishStatus == null ||
                option.publishStatus === 'PUBLISHED' ||
                option.value === selectedValue) &&
            (option.value === selectedValue ||
                option.forecastYear == null ||
                budgetYear == null ||
                String(option.forecastYear) === String(budgetYear))
        );
        setFilteredForecasts(options);
    }, [
        forecastOptions,
        plannedForecastsOptions,
        demandPlanningOptions,
        budget?.forecast_id,
        budget?.period,
    ]);
    useEffect(() => {
        if (isOpenViewBudgetAndForecast) {
            dispatch(getBudgetForecastsData({
                budgetId: currentBudget, 
            }))
        }
    }, [
      isOpenViewBudgetAndForecast,
      currentBudget
    ])

    const totalsData = profitAndLossReportByForecast?.totals;
    const tableData = profitAndLossReportByForecast?.tableData;
    const totalsByRows = profitAndLossReportByForecast?.totalsByRows;

    const handleExport = async (tableKey) => {
        try {
            setExportingTable(tableKey);
            const params = new URLSearchParams({
                table: tableKey,
                ...(currentBudget ? { budgetId: currentBudget || "" } : {}),
            });
            const response = await fetch(`/api/v1/profit-and-loss-new/budget-and-forecast/export?${params}`);
            
            if (!response.ok) throw new Error('Export failed');
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `profit_and_loss_${currentBudget}_${tableKey}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export:', error);
        } finally {
            setExportingTable(null);
        }
    };

    const handleExportAll = async () => {
        try {
            setIsExporting(true);
            const params = new URLSearchParams({
                ...(currentBudget ? { budgetId: currentBudget || "" } : {}),
            });
            const response = await fetch(`/api/v1/profit-and-loss-new/budget-and-forecast/export-all?${params}`);
            
            if (!response.ok) throw new Error('Export failed');
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `profit_and_loss_${currentBudget}_all_tables.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export:', error);
        } finally {
            setIsExporting(false);
        }
    };

    const budgetSettingsIsLoading = () => 
        budgetLoading && budgetLoadingKeys.includes(BudgetLoadingKeys.getBudgetSettings);
    const forecastOptionsIsLoading = () => 
        forecastLoading && forecastLoadingKeys.includes(ForecastLoadingKeys.getForecastOptions);
    const getPlannedForecastOptionsIsLoading = () =>
        forecastPlanningLoading && forecastPlanningLoadingKeys.includes(ForecastPlanningLoadingKeys.getPlannedForecastsOptions);
    const demandPlanningOptionsIsLoading = () =>
        forecastLoading && forecastLoadingKeys.includes(ForecastLoadingKeys.getDemandPlanningOptions);
    const getForecastsIsLoading = () => 
        forecastLoading && forecastLoadingKeys.includes(ForecastLoadingKeys.getForecasts);
    const getCardIsLoading = () => 
        budgetLoading && budgetLoadingKeys.includes(BudgetLoadingKeys.getBudgetCard);
    const deleteBudgetIsLoading = () =>
        budgetLoading && budgetLoadingKeys.includes(BudgetLoadingKeys.deleteBudget);
    const deleteForecastIsLoading = () =>
        forecastLoading && forecastLoadingKeys.includes(ForecastLoadingKeys.deleteForecast);
    const budgetForecastOptionsIsLoading = () =>
        budgetForecastLoading && budgetForecastLoadingKeys.includes(ProfitAndLossNewLoadingKeys.getBudgetForecastsOptions);
    const budgetBudgetForecastIsLoading = () =>
        budgetForecastLoading && budgetForecastLoadingKeys.includes(ProfitAndLossNewLoadingKeys.getBudgetForecastsData);

    return (
        <>
            <main className="w-full h-auto px-4 sm:px-6 lg:px-8 mb-10">
                {!isOpenViewBudgetAndForecast && (
                    <>
                        <div className="h-auto py-6 pb-0 flex flex-col">
                            <div className="flex items-center mb-8 justify-between gap-4 flex-wrap">
                                <div className="flex items-center space-x-4">
                                    <div className="flex items-center space-x-2">
                                        <h1 className="text-2xl font-semibold text-gray-900">Budget</h1>
                                    </div>
                                </div>
                                {access === "edit" && (
                                    <div className="flex flex-row gap-1">
                                        <button
                                            type="button"
                                            onClick={() => setBudgetSettingsSidebarOpen(true)}
                                            className="flex h-8 flex-row items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-700 whitespace-nowrap transition-colors hover:bg-gray-50"
                                        >
                                            <Cog8ToothIcon className="h-4 w-4 text-gray-400" />
                                            <span>Budget Settings</span>
                                        </button>
                                        <button
                                            type="button"
                                            className="flex h-8 flex-row items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-700 whitespace-nowrap transition-colors hover:bg-gray-50"
                                            onClick={() => {
                                                setIsOpenViewBudgetAndForecast(true);
                                            }}
                                        >
                                            <EyeIcon className="h-4 w-4 text-gray-400" />
                                            <span>View Budget</span>
                                        </button>
                                        {/* <div className="flex flex-row items-center gap-2 flex-wrap">
                                            <button
                                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                                onClick={() => {
                                                    setForecast({});
                                                }}
                                            >
                                                <span>Create Forecast</span>
                                            </button>
                                        </div> */}
                                        <button
                                            type="button"
                                            className="flex h-8 flex-row items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-700 whitespace-nowrap transition-colors hover:bg-gray-50"
                                            onClick={() => {
                                                setBudget({
                                                    active_categories: reportCategories.map((category) => category.id),
                                                });
                                            }}
                                        >
                                            <PlusIcon className="h-4 w-4 text-gray-400" />
                                            <span>Create Budget</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        {(!budgetFirstLoadComplete || !forecastFirstLoadComplete) ? (
                            <LoaderSpinner width="70" height="70" border="5" />
                        ) : (
                            <div className="w-full flex flex-col gap-4">
                                {/* <section className="flex flex-col gap-2">
                                    <h4 className="text-xl font-bold">Forecasts</h4>
                                    <div className="relative">
                                        {getForecastsIsLoading() && <OverlayGrayBackground />}
                                        <div className="w-full">
                                            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                                                <TanStackTable
                                                    data={forecasts || []}
                                                    hasActions={true}
                                                    onRowClick={(row) => {
                                                        navigate(`${ROUTES.FORECAST}?id=${row.original.id}`);
                                                    }}
                                                    columns={[
                                                        { accessorKey: "id", header: "ID", enableSorting: false, headerTrClassName: "w-[60px]" },
                                                        { accessorKey: "name", header: "Forecast Name", enableSorting: false, headerTrClassName: "min-w-[260px]" },
                                                        { accessorKey: "period", header: "Period", enableSorting: false, headerTrClassName: "min-w-[260px]" },
                                                        { accessorKey: "", header: " ", enableSorting: false, headerTrClassName: "min-w-[260px]", cell: ({ row }) => {
                                                            return (
                                                                <div />
                                                            )
                                                        }},
                                                        access === "edit" ? {
                                                            accessorKey: "actions", headerTrClassName: "min-w-[80px]", headerClassName: "justify-end", header: "Actions", enableSorting: false, cell: ({ row }) => {
                                                                return (
                                                                    <div className={`flex gap-2 justify-end ${row.index % 2 === 0 ? '!bg-white' : '!bg-gray-50'}`}>
                                                                        {access === "edit" && (
                                                                            <>
                                                                                <button
                                                                                    className="inline-flex items-center px-3 py-1.5 border border-transparent rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"      
                                                                                >
                                                                                    <EyeIcon className="h-4 w-4" />
                                                                                </button>
                                                                                <button
                                                                                    className="inline-flex items-center px-3 py-1.5 border border-transparent rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"  
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setForecast(row.original);
                                                                                    }} 
                                                                                >
                                                                                    <PencilIcon className="h-4 w-4" />
                                                                                </button>
                                                                                <button
                                                                                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 rounded border border-black disabled:opacity-50"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setForecastIdToDelete(row.original.id);
                                                                                        setDeleteForecastModalIsOpen(true);
                                                                                    }}
                                                                                >
                                                                                    {(deleteForecastIsLoading() && forecastIdToDelete === row.original.id) ? (
                                                                                        <ArrowPathIcon className="animate-spin h-4 w-4" />
                                                                                    ) : (
                                                                                        <TrashIcon className="h-4 w-4" aria-hidden="true" />
                                                                                    )}
                                                                                </button>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                )
                                                            }
                                                        } : null,
                                                    ].filter(Boolean)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </section> */}
                                <section className="flex flex-col gap-2">
                                    <h4 className="text-xl font-bold">Budgets</h4>
                                    <div className="relative">
                                        {getCardIsLoading() && <OverlayGrayBackground />}
                                        <div className="w-full">
                                            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                                                <TanStackTable
                                                    data={budgetCards || []}
                                                    hasActions={true}
                                                    compact={true}
                                                    onRowClick={(row) => {
                                                        navigate(`${ROUTES.BUDGET}?id=${row.original.id}`);
                                                    }}
                                                    columns={[
                                                        { accessorKey: "id", header: "ID", enableSorting: false, headerTrClassName: "w-[60px]" },
                                                        { accessorKey: "name", header: "Budget Name", enableSorting: false, headerTrClassName: "min-w-[260px]" },
                                                        { accessorKey: "period", header: "Period", enableSorting: false, headerTrClassName: "min-w-[260px]" },
                                                        { accessorKey: "publish_status", header: "Status", enableSorting: false, headerTrClassName: "min-w-[140px]", cell: ({ row }) => {
                                                            const isPublished = row.original.publish_status === "PUBLISHED";
                                                            return (
                                                                <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 ${
                                                                    isPublished
                                                                        ? "border border-green-200 bg-green-50 text-[11px] font-semibold text-green-700"
                                                                        : "bg-gray-100 text-xs font-medium text-gray-600"
                                                                }`}>
                                                                    {isPublished && <span className="h-1.5 w-1.5 rounded-full bg-green-500" />}
                                                                    {row.original.publish_status || "DRAFT"}
                                                                </span>
                                                            );
                                                        } },
                                                        { accessorKey: "forecast_id", header: "Connected Forecast", enableSorting: false, headerTrClassName: "min-w-[260px]", cell: ({ row }) => {
                                                            const connectedForecast = (filteredForecasts || []).find(
                                                                (forecast) => forecast.value === row.original.forecast_id
                                                            );
                                                            const connectedDemandPlan = (demandPlanningOptions || []).find(
                                                                (plan) => `${plan.id}-demandplan` === row.original.forecast_id
                                                            );
                                                            return (
                                                                <div>
                                                                    {connectedForecast?.label || (
                                                                        connectedDemandPlan
                                                                            ? `${connectedDemandPlan.name} ${
                                                                                connectedDemandPlan.publish_status === 'PUBLISHED'
                                                                                    ? '(D)'
                                                                                    : '(D – Draft)'
                                                                            }`
                                                                            : null
                                                                    )}
                                                                </div>
                                                            )
                                                        }},
                                                        access === "edit" ? {
                                                            accessorKey: "actions", headerTrClassName: "min-w-[80px]", headerClassName: "justify-end", header: "Actions", enableSorting: false, cell: ({ row }) => {
                                                                return (
                                                                    <div className={`flex gap-2 justify-end ${row.index % 2 === 0 ? '!bg-white' : '!bg-gray-50'}`}>
                                                                        {access === "edit" && (
                                                                            <>
                                                                                <button
                                                                                    className="h-7 w-7 flex items-center justify-center hover:bg-gray-100 rounded"
                                                                                >
                                                                                    <EyeIcon className="h-3.5 w-3.5 text-gray-900" />
                                                                                </button>
                                                                                <button
                                                                                    className="h-7 w-7 flex items-center justify-center hover:bg-gray-100 rounded"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setDuplicateBudget(row.original);
                                                                                    }}
                                                                                >
                                                                                    <DocumentDuplicateIcon className="h-3.5 w-3.5 text-gray-900" />
                                                                                </button>
                                                                                <button
                                                                                    className="h-7 w-7 flex items-center justify-center hover:bg-gray-100 rounded"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setBudget(row.original);
                                                                                    }}   
                                                                                >
                                                                                    <PencilIcon className="h-3.5 w-3.5 text-gray-900" />
                                                                                </button>
                                                                                <button
                                                                                    className="h-7 w-7 flex items-center justify-center hover:bg-red-50 rounded disabled:opacity-50"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setBudgetIdToDelete(row.original.id);
                                                                                        setDeleteBudgetModalIsOpen(true);
                                                                                    }}
                                                                                >
                                                                                    {(deleteBudgetIsLoading() && budgetIdToDelete === row.original.id) ? (
                                                                                        <ArrowPathIcon className="animate-spin h-3.5 w-3.5 text-red-600" />
                                                                                    ) : (
                                                                                        <TrashIcon className="h-3.5 w-3.5 text-red-600" aria-hidden="true" />
                                                                                    )}
                                                                                </button>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                )
                                                            }
                                                        } : null,
                                                    ].filter(Boolean)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        )}
                    </>
                )}
                {isOpenViewBudgetAndForecast && access === "edit" && (
                    <section>
                        <div className="h-auto py-6 pb-0 flex flex-col">
                            <div className="flex items-center mb-8 justify-between gap-4 flex-wrap">
                                <div className="flex items-center space-x-4">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                                            <ChartBarSquareIcon className="h-6 w-6 text-white"/>
                                        </div>
                                        <h1 className="text-xl font-semibold text-gray-900">KINGS LOOT</h1>
                                    </div>
                                    <div className="bg-green-100 text-green-800 border-green-200 px-4 py-0.5 rounded-md font-bold text-sm whitespace-nowrap">
                                        Budget
                                    </div>
                                </div>
                                <div className="flex flex-row items-center gap-2 flex-wrap">
                                    {(
                                        <>
                                            <div className="flex flex-row gap-2 items-center">
                                                <span>Budget:</span>
                                                <div className="w-48">
                                                    <SelectDropdown
                                                        options={budgetOptions}
                                                        value={currentBudget}
                                                        onChange={setCurrentBudget}
                                                        disabled={budgetForecastOptionsIsLoading()}
                                                    />
                                                </div>
                                            </div>
                                            <button 
                                                onClick={handleExportAll}
                                                disabled={isExporting || budgetForecastOptionsIsLoading()}
                                                className="flex bg-primary transition-all duration-300 rounded-lg justify-between gap-2 items-center text-white text-sm rounded-lg outline-none p-2.5 px-3 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {isExporting ? (
                                                    <>
                                                        <LoaderSpinner width="16" height="16" border="2" parentHeight="h-4" />
                                                        <span>Exporting...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <ArrowDownTrayIcon className="h-4 w-4" />
                                                        <span>Export All</span>
                                                    </>
                                                )}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setCurrentBudget(null);
                                                    setIsOpenViewBudgetAndForecast(false);
                                                }}
                                                className="flex justify-center items-center bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg outline-none p-2.5 px-3 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <XMarkIcon className="h-5 w-5" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <>
                            <section className="grid gap-4 mb-8 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
                                {totalsData?.["revenue"] && (
                                    <CardTotal
                                        data={totalsData?.["revenue"]}
                                        Icon={CurrencyDollarIcon}
                                        wrapperClassname="border-green-200 bg-gradient-to-br from-green-50 to-green-100"
                                        titleClassname="text-green-600"
                                        priceClassname="text-green-900"
                                        iconClassname="text-green-600"
                                        isLoading={budgetBudgetForecastIsLoading()}
                                    />
                                )}
                                {totalsData?.["contribution-margin"] && (
                                    <CardTotal
                                        data={totalsData?.["contribution-margin"]}
                                        Icon={ArrowTrendingUpIcon}
                                        wrapperClassname="border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100"
                                        titleClassname="text-blue-600"
                                        priceClassname="text-blue-900"
                                        iconClassname="text-blue-600"
                                        percentageClassname="text-blue-700"
                                        percentageLabel={"Contribution Ratio: "}
                                        isLoading={budgetBudgetForecastIsLoading()}
                                    />
                                )}
                                {totalsData?.["net-income"] && (
                                    <CardTotal
                                        data={totalsData?.["net-income"]}
                                        Icon={ChartBarSquareIcon}
                                        wrapperClassname="border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100"
                                        titleClassname="text-purple-600"
                                        priceClassname="text-purple-900"
                                        iconClassname="text-purple-600"
                                        percentageLabel={"Net Profit: "}
                                        percentageClassname="text-purple-700"
                                        isLoading={budgetBudgetForecastIsLoading()}
                                    />
                                )}
                                {totalsData?.["beroas"] && (
                                    <CardTotal
                                        data={totalsData?.["beroas"]}
                                        varianceDirection={LOWER_IS_BETTER}
                                        Icon={ArrowTrendingUpIcon}
                                        wrapperClassname="border-orange-200 bg-gradient-to-br from-orange-50 to-orange-100"
                                        titleClassname="text-orange-600"
                                        priceClassname="text-orange-900"
                                        iconClassname="text-orange-600"
                                        isLoading={budgetBudgetForecastIsLoading()}
                                        withoutTransform={true}
                                    />
                                )}
                                {totalsData?.["ad-spend"] && (
                                    <CardTotal
                                        data={totalsData?.["ad-spend"]}
                                        varianceDirection={LOWER_IS_BETTER}
                                        Icon={ChartBarSquareIcon}
                                        wrapperClassname="border-red-200 bg-gradient-to-br from-red-50 to-red-100"
                                        titleClassname="text-red-600"
                                        priceClassname="text-red-900"
                                        iconClassname="text-red-600"
                                        percentageLabel={"% of Revenue: "}
                                        percentageClassname="text-red-700"
                                        isLoading={budgetBudgetForecastIsLoading()}
                                    />
                                )}
                                {totalsData?.["mer"] && (
                                    <CardTotal
                                        data={totalsData?.["mer"]}
                                        Icon={ArrowTrendingUpIcon}
                                        wrapperClassname="border-teal-200 bg-gradient-to-br from-teal-50 to-teal-100"
                                        titleClassname="text-teal-600"
                                        priceClassname="text-teal-900"
                                        iconClassname="text-teal-600"
                                        isLoading={budgetBudgetForecastIsLoading()}
                                        withoutTransform={true}
                                    />
                                )}
                            </section>
                            <section className="relative h-full mb-8">
                                {budgetBudgetForecastIsLoading() ? (
                                    <div className="h-5/6 w-full absolute mt-8">
                                        <LoaderSpinner colorSpinner="black" width="70" height="70" border="5" />
                                    </div>
                                ) : (
                                <>
                                    <div className="overflow-x-auto">
                                        <div className="space-y-8">
                                            {tableData?.["summary"] && (
                                                <ProfitLossTable
                                                    key={`${budget}_${getTableData("summary", "budget-forecast")}`}
                                                    title={getTableData("summary", "budget-forecast")}
                                                    data={tableData["summary"]}
                                                    type={"budget"}
                                                    compareType=""
                                                    totalsByRows={totalsByRows}
                                                    onExport={handleExport}
                                                    isExporting={exportingTable === "summary"}
                                                    tableKey="summary"
                                                />
                                            )}
                                            {(tableData?.["revenue-breakdown"] || tableData?.["contribution-margin"]) && (
                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                                    {tableData?.["revenue-breakdown"] && (
                                                        <ProfitLossTable
                                                            key={`${budget}_${getTableData("revenue-breakdown")}`}
                                                            title={getTableData("revenue-breakdown")}
                                                            data={tableData["revenue-breakdown"]}
                                                            type={"budget"}
                                                            compareType=""
                                                            totalsByRows={totalsByRows}
                                                            onExport={handleExport}
                                                            isExporting={exportingTable === "revenue-breakdown"}
                                                            tableKey="revenue-breakdown"
                                                        />
                                                    )}
                                                    {tableData?.["contribution-margin"] && (
                                                        <ProfitLossTable
                                                            key={`${budget}_${getTableData("contribution-margin")}`}
                                                            title={getTableData("contribution-margin")}
                                                            data={tableData["contribution-margin"]}
                                                            type={"budget"}
                                                            compareType=""
                                                            totalsByRows={totalsByRows}
                                                            onExport={handleExport}
                                                            isExporting={exportingTable === "contribution-margin"}
                                                            tableKey="contribution-margin"
                                                        />
                                                    )}
                                                </div>
                                            )}
                                            {tableData?.["total-cost-of-goods-sold"] && (
                                                <ProfitLossTable
                                                    key={`${budget}_${getTableData("total-cost-of-goods-sold")}`}
                                                    title={getTableData("total-cost-of-goods-sold")}
                                                    data={tableData["total-cost-of-goods-sold"]}
                                                    type={"budget"}
                                                    compareType=""
                                                    totalsByRows={totalsByRows}
                                                    onExport={handleExport}
                                                    isExporting={exportingTable === "total-cost-of-goods-sold"}
                                                    tableKey="total-cost-of-goods-sold"
                                                />
                                            )}
                                            {tableData?.["total-operating-expenses"] && (
                                                <ProfitLossTable
                                                    key={`${budget}_${getTableData("total-operating-expenses")}`}
                                                    title={getTableData("total-operating-expenses")}
                                                    data={tableData["total-operating-expenses"]}
                                                    type={"budget"}
                                                    compareType=""
                                                    totalsByRows={totalsByRows}
                                                    onExport={handleExport}
                                                    isExporting={exportingTable === "total-operating-expenses"}
                                                    tableKey="total-operating-expenses"
                                                />
                                            )}
                                            {tableData?.["net-operating-income"] && (
                                                <ProfitLossTable
                                                    key={`${budget}_${getTableData("net-operating-income")}`}
                                                    title={getTableData("net-operating-income")}
                                                    data={tableData["net-operating-income"]}
                                                    type={"budget"}
                                                    compareType=""
                                                    totalsByRows={totalsByRows}
                                                    onExport={handleExport}
                                                    isExporting={exportingTable === "net-operating-income"}
                                                    tableKey="net-operating-income"
                                                />
                                            )}
                                            {tableData?.["net-income-summary"] && (
                                                <ProfitLossTable
                                                    key={`${budget}_${getTableData("net-income-summary")}`}
                                                    title={getTableData("net-income-summary")}
                                                    data={tableData["net-income-summary"]}
                                                    type={"budget"}
                                                    compareType=""
                                                    totalsByRows={totalsByRows}
                                                    onExport={handleExport}
                                                    isExporting={exportingTable === "net-income-summary"}
                                                    tableKey="net-income-summary"
                                                    className="border-green-300 bg-green-50"
                                                />
                                            )}
                                        </div>
                                    </div>
                                </>
                                )}
                            </section>
                        </>
                    </section>
                )}
            </main>
            <ForecastEditModal
                key={`${forecast?.name}_${forecast?.period}_${forecast?.id}`}
                isOpen={access === "edit" && !!forecast}
                onClose={() => {
                    setForecast(null);
                }}
                labelItem={forecast?.name}
                periodItem={forecast?.period}
                onAccept={async ({ name, period }) => {
                    await dispatch(createForecastRequest({ forecastId: forecast?.id, name, period }));
                    setForecast(null);
                    dispatch(getForecasts({}));
                }}
            />
            <DuplicateBudgetModal
                isOpen={access === "edit" && !!duplicateBudget}
                onClose={() => {
                    setDuplicateBudget(null);
                }}
                budgetName={duplicateBudget?.name}
                onAccept={async ({ name, copyAll, copyValues, copyRollups, copyFormulas }) => {
                    await dispatch(duplicateBudgetRequest({ 
                        budgetId: duplicateBudget?.id, 
                        name,
                        copyAll,
                        copyValues,
                        copyRollups,
                        copyFormulas
                    }));
                    setDuplicateBudget(null);
                    dispatch(getBudgetCards({}));
                    dispatch(getBudgetForecastsOptions({}));
                }}
            />
            <BudgetModal
                key={`${budget?.name}_${budget?.period}_${budget?.forecast_id}`}
                isOpen={access === "edit" && !!budget}
                onClose={() => {
                    setBudget(null);
                }}
                showTemplateEditor={true}
                templateOptions={(budgetSettings?.templates || [])
                    .filter((template) => template?.items?.structure?.status !== 'DRAFT')
                    .map((template) => {
                    return {
                        label: template?.name,
                        value: template?.id
                    }
                })}
                netIncomeOptions={(budgetSettings?.netIncomeCalculations || []).map((netIncome) => {
                    return {
                        label: netIncome?.name,
                        value: netIncome?.id
                    }
                })}
                netIncomeOptionsLoading={budgetSettingsIsLoading()}
                labelItem={budget?.name}
                periodItem={budget?.period}
                selectedNetIncomeItem={budget?.net_income_calculation_id}
                forecastOptionsLoading={forecastOptionsIsLoading() || demandPlanningOptionsIsLoading() || getPlannedForecastOptionsIsLoading()}
                selectedForecastItem={budget?.forecast_id}
                activeCategories={budget?.active_categories}
                reportCategories={reportCategories}
                forecastOptions={filteredForecasts}
                onAccept={async ({ name, period, selectedForecast, activeCategories, selectedTemplate, selectedNetIncome }) => {
                    await dispatch(createBudgetRequest({ id: budget?.id || null, name, period, selectedForecast, activeCategories, selectedTemplate, selectedNetIncome }));
                    setBudget(null);
                    dispatch(getBudgetCards({}));
                    dispatch(getBudgetForecastsOptions({}))
                }}
            />
            <ConfrimModal
                isOpen={(deleteBudgetModalIsOpen) && access === "edit"}
                title={
                    `Are you sure want to delete this budget?`
                }
                isLoading={deleteBudgetIsLoading()}
                confirmButtonTitle={"Delete"}
                submitClick={async () => {
                    if (deleteBudgetModalIsOpen) {
                        await dispatch(deleteBudgetRequest({ id: budgetIdToDelete }));
                    }
                    setBudgetIdToDelete("");
                    setDeleteBudgetModalIsOpen(false);
                    dispatch(getBudgetCards({}));
                    dispatch(getBudgetForecastsOptions({}))
                }}
                cancelClick={() => {
                    setBudgetIdToDelete("");
                    setDeleteBudgetModalIsOpen(false);
                }}
            />
            <ConfrimModal
                isOpen={deleteForecastModalIsOpen && access === "edit"}
                title={
                    `Are you sure want to delete this forecast?`
                }
                isLoading={deleteForecastIsLoading()}
                confirmButtonTitle={"Delete"}
                submitClick={async () => {
                    await dispatch(deleteForecastRequest({ id: forecastIdToDelete }));
                    setForecastIdToDelete("");
                    setDeleteForecastModalIsOpen(false);
                    dispatch(getForecasts({}));
                }}
                cancelClick={() => {
                    setForecastIdToDelete("");
                    setDeleteForecastModalIsOpen(false);
                }}
            />
            <BudgetSettingsSidebar
                isOpen={budgetSettingsSidebarOpen}
                onClose={() => setBudgetSettingsSidebarOpen(false)}
                onCategoryChange={async () => {
                    await dispatch(getReportCategories({}));
                    await dispatch(getBudgetCards({}));
                }}
            />
        </>
    )
}

export default BudgetForecast;